/**
 * Reusable expression input with column autocomplete + live validity.
 * Shared by the filter and derived-signal Expr modes. Column suggestions accept
 * on Enter or Tab. The committed value is owned by the parent (via `value` +
 * the `change` callback); this component only handles editing, the caret-aware
 * column suggestions, and the ✓/✗ status line.
 *
 * `validate` is injected so the same widget serves boolean filters and numeric
 * derived expressions. The backend re-validates authoritatively - this is UX.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../lit-base.ts';
import { fuzzyScore } from '../utils/fuzzy.ts';
import { caretToken, wrapColumnForExpr, type CaretToken } from '../features/filters/expr-parser.ts';
import type { ColumnInfo } from '../api/types.ts';

@customElement('expr-input')
export class ExprInput extends LightElement {
  /** Columns offered for autocomplete (already filtered/sorted by the parent). */
  @property({ attribute: false }) columns: ColumnInfo[] = [];
  /** Committed expression text (owned by the parent). */
  @property() value = '';
  @property() placeholder = '';
  @property() label = '';
  @property({ type: Boolean }) experimental = false;
  /** Returns an error message, or null when the text is valid/blank. */
  @property({ attribute: false }) validate: (text: string) => string | null = () => null;
  /** Called with the new text on every edit. */
  @property({ attribute: false }) change: (value: string) => void = () => {};
  /** Optional help panel; when set a `?` toggle is shown. */
  @property({ attribute: false }) help: TemplateResult | null = null;

  @state() private showHelp = false;
  @state() private suggestions: ColumnInfo[] = [];
  @state() private sugIndex = -1;
  private caretTok: CaretToken | null = null;

  private onInput(e: Event): void {
    const input = e.target as HTMLInputElement;
    this.change(input.value);
    this.refreshSuggestions(input);
  }

  private refreshSuggestions(input: HTMLInputElement): void {
    const caret = input.selectionStart ?? input.value.length;
    const tok = caretToken(input.value, caret);
    if (!tok) {
      this.clearSuggestions();
      return;
    }
    const q = tok.fragment.trim();
    let matches: ColumnInfo[];
    if (!q) {
      matches = this.columns.slice(0, 12);
    } else {
      const scored: Array<{ c: ColumnInfo; s: number }> = [];
      for (const c of this.columns) {
        const s = fuzzyScore(q, c.name);
        if (s !== null) scored.push({ c, s });
      }
      scored.sort((a, b) => b.s - a.s);
      matches = scored.slice(0, 12).map((x) => x.c);
    }
    this.caretTok = tok;
    this.suggestions = matches;
    this.sugIndex = matches.length ? 0 : -1;
  }

  private clearSuggestions(): void {
    this.suggestions = [];
    this.sugIndex = -1;
    this.caretTok = null;
  }

  private pick(name: string): void {
    const tok = this.caretTok;
    if (!tok) return;
    const insert = wrapColumnForExpr(name);
    const next = this.value.slice(0, tok.start) + insert + this.value.slice(tok.end);
    this.change(next);
    this.clearSuggestions();
    const pos = tok.start + insert.length;
    void this.updateComplete.then(() => {
      const el = this.querySelector<HTMLInputElement>('.expr-input');
      if (el) {
        el.focus();
        el.setSelectionRange(pos, pos);
      }
    });
  }

  private onKeydown(e: KeyboardEvent): void {
    if (!this.suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.sugIndex = Math.min(this.suggestions.length - 1, this.sugIndex + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.sugIndex = Math.max(0, this.sugIndex - 1);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      // Tab and Enter both accept the highlighted column. preventDefault stops
      // Tab from moving focus out of the field before the pick lands.
      const c = this.suggestions[this.sugIndex];
      if (!c) return; // nothing highlighted - let Tab move focus normally
      e.preventDefault();
      this.pick(c.name);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.clearSuggestions();
    }
  }

  override render(): TemplateResult {
    const err = this.value.trim() ? this.validate(this.value) : null;
    const hasHead = this.label || this.help;
    return html`
      <div class="expr-input-block">
        ${hasHead
          ? html`<div class="expr-head">
              ${this.label
                ? html`<span class="expr-title"
                    >${this.label}${this.experimental
                      ? html` <span class="expr-exp">experimental</span>`
                      : nothing}</span
                  >`
                : nothing}
              ${this.help
                ? html`<button
                    class="expr-help-btn ${this.showHelp ? 'active' : ''}"
                    title="What's allowed?"
                    @click=${() => (this.showHelp = !this.showHelp)}
                  >
                    ?
                  </button>`
                : nothing}
            </div>`
          : nothing}
        ${this.showHelp && this.help ? html`<div class="expr-help">${this.help}</div>` : nothing}
        <div class="expr-input-wrap">
          <input
            class="expr-input"
            type="text"
            autocomplete="off"
            spellcheck="false"
            placeholder=${this.placeholder}
            .value=${this.value}
            @input=${this.onInput}
            @keydown=${this.onKeydown}
            @blur=${() => setTimeout(() => this.clearSuggestions(), 150)}
          />
          ${this.suggestions.length
            ? html`<div class="expr-suggest">
                ${this.suggestions.map(
                  (c, i) => html`<div
                    class="expr-suggest-item ${i === this.sugIndex ? 'active' : ''}"
                    @mousedown=${(e: Event) => {
                      e.preventDefault();
                      this.pick(c.name);
                    }}
                    @mouseenter=${() => (this.sugIndex = i)}
                  >
                    ${c.name}
                  </div>`,
                )}
              </div>`
            : nothing}
        </div>
        ${this.value.trim()
          ? err
            ? html`<div class="expr-status is-error">✗ ${err}</div>`
            : html`<div class="expr-status is-ok">✓ valid</div>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'expr-input': ExprInput;
  }
}
